import { Popover, Portal, Stack } from '@chakra-ui/react';
import { LuActivity, LuCircleCheck, LuCircleX, LuTriangleAlert } from 'react-icons/lu';
import { Badge, Box, Button, HStack, Spinner, Text } from '../../../components/ui/chakraPrimitives';
import { formatDisplayUtcDateTime } from '../../../util/dates';
import { useEmailAutomationWorkerHealthQuery } from '../apis/useEmailAutomationWorkerHealthQuery';
import { EmailAutomationWorkerHealth } from '../types/EmailAutomationWorkerHealth';
import { getAutomationBadgeSummary, getWorkerStatusColorPalette } from '../utils/automationStatusBadgeUtils';

export const AutomationStatusBadge = () => {
  const query = useEmailAutomationWorkerHealthQuery();
  const workers = query.data ?? [];
  const summary = getAutomationBadgeSummary(query.data, query.isError);
  const affectedCount = summary.unhealthyCount + summary.warningCount;
  const Icon =
    summary.severity === 'healthy' ? LuCircleCheck : summary.severity === 'warning' ? LuTriangleAlert : LuCircleX;

  return (
    <Popover.Root positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <Popover.Trigger asChild>
        <Button
          size='xs'
          variant='outline'
          colorPalette={summary.colorPalette}
          aria-label={`Email automation status: ${summary.label}`}
          minW='54px'
          px='2'
          gap='1.5'
          justifyContent='center'
          whiteSpace='nowrap'>
          {query.isLoading ? <Spinner size='xs' /> : <Icon />}
          <Text as='span' fontSize='xs' fontWeight='semibold' lineHeight='1'>
            {affectedCount}
          </Text>
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content width='380px' maxW='calc(100vw - 24px)'>
            <Popover.Arrow />
            <Popover.Body>
              <Stack gap='3'>
                <HStack justifyContent='space-between' alignItems='center'>
                  <HStack gap='2'>
                    <LuActivity />
                    <Text fontWeight='semibold'>Email Automation</Text>
                  </HStack>
                  <Badge colorPalette={summary.colorPalette} variant='subtle'>
                    {summary.totalCount}
                  </Badge>
                </HStack>
                {query.isError ? (
                  <Text fontSize='sm' color='red.700'>
                    Worker status is unavailable.
                  </Text>
                ) : (
                  <Stack gap='2' maxH='420px' overflowY='auto'>
                    {workers.map((worker) => (
                      <WorkerHealthRow key={worker.WorkerKey} worker={worker} />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

const WorkerHealthRow = ({ worker }: { worker: EmailAutomationWorkerHealth }) => {
  const colorPalette = getWorkerStatusColorPalette(worker.Status);
  const lastActivity =
    worker.LastFailedAtUtc ?? worker.LastTimedOutAtUtc ?? worker.LastCompletedAtUtc ?? worker.LastStartedAtUtc;

  return (
    <HStack
      alignItems='flex-start'
      justifyContent='space-between'
      gap='3'
      borderTopWidth='1px'
      borderColor='gray.100'
      pt='2'>
      <Box minW='0'>
        <Text fontSize='sm' fontWeight='medium' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
          {worker.DisplayName}
        </Text>
        <Text fontSize='xs' color='gray.600'>
          {lastActivity ? formatDisplayUtcDateTime(lastActivity) : 'No activity yet'}
        </Text>
      </Box>
      <Stack gap='1' alignItems='flex-end' flexShrink={0}>
        <Badge size='xs' variant='subtle' colorPalette={colorPalette}>
          {worker.Status}
        </Badge>
        {worker.ConsecutiveFailures > 0 || worker.TimeoutCount > 0 ? (
          <Text fontSize='xs' color='gray.600'>
            {worker.ConsecutiveFailures} fail / {worker.TimeoutCount} timeout
          </Text>
        ) : null}
      </Stack>
    </HStack>
  );
};
